import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, BookOpen, Mail, Menu, X, Calculator, Code } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import { splitLanguage, translator } from '../utils/i18n';

export default function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const { lang } = splitLanguage(useLocation().pathname);
    const t = translator(lang);

    // Only the home page has a translated counterpart, so only its link carries
    // the language prefix. The blog, widgets, contact and calculator pages are
    // English, and linking them at an /es address would invite Google to index
    // English prose on a Spanish URL.
    const homePath = lang === 'en' ? '/' : `/${lang}`;

    const navItems = [
        { path: homePath, label: t('nav.home', 'Home'), icon: Home },
        { path: '/widgets', label: t('nav.widgets', 'Widgets'), icon: Code },
        { path: '/blog', label: t('nav.blog', 'Blog'), icon: BookOpen },
        { path: '/contact', label: t('nav.contact', 'Contact'), icon: Mail },
    ];

    return (
        <nav className="sticky top-0 z-50 w-full border-b border-white/10 bg-white dark:bg-slate-900 backdrop-blur-md transition-colors duration-300">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <NavLink
                        to={homePath}
                        aria-label={t('nav.homeAria', 'Simple Unit Converter Home')}
                        className="flex items-center space-x-2 text-slate-800 dark:text-white hover:opacity-80 transition-opacity"
                        onClick={() => setIsOpen(false)}
                    >
                        <div className="p-1.5 bg-emerald-500/10 rounded-lg">
                            <Calculator className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <span className="font-bold text-lg hidden sm:block">SimpleUnitConverter</span>
                    </NavLink>

                    {/* Desktop Navigation */}
                    {/* lg, not md: at 768px the full row does not fit even in English, and
                        the translated labels ("Kraftstoffverbrauch", "الأدوات المدمجة")
                        are longer still. Below this width the menu button is used. */}
                    <div className="hidden lg:flex items-center space-x-8">
                        <div className="flex items-center space-x-1">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) => `
                    flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                    ${isActive
                                            ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10'
                                            : 'text-slate-700 dark:text-slate-200 hover:text-emerald-700 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-white/5'
                                        }
                  `}
                                >
                                    <item.icon className="w-4 h-4" />
                                    <span>{item.label}</span>
                                </NavLink>
                            ))}
                            <NavLink
                                to="/calculator"
                                className={({ isActive }) => `
                  flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  ${isActive
                                        ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10'
                                        : 'text-slate-700 dark:text-slate-200 hover:text-emerald-700 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-white/5'
                                    }
                `}
                            >
                                <Calculator className="w-4 h-4" />
                                <span>{t('nav.calculator', 'Calculator')}</span>
                            </NavLink>
                        </div>
                        {/* Logical properties, so the divider sits on the
                            leading edge in Arabic too rather than the left. */}
                        <div className="flex items-center gap-3 ps-6 border-s border-slate-200 dark:border-white/10">
                            <LanguageSwitcher />
                            <ThemeToggle />
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="flex items-center space-x-3 lg:hidden">
                        <LanguageSwitcher compact />
                        <ThemeToggle />
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            aria-label={t('nav.toggleMenu', 'Toggle menu')}
                            className="p-2 rounded-lg text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        >
                            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            <div className={`
        lg:hidden border-t border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 overflow-hidden transition-all duration-300 ease-in-out
        ${isOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}
      `}>
                <div className="container mx-auto px-4 py-4 space-y-2">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsOpen(false)}
                            className={({ isActive }) => `
                flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors duration-200
                ${isActive
                                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                    : 'text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-white/5'
                                }
              `}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                        </NavLink>
                    ))}
                    <NavLink
                        to="/calculator"
                        onClick={() => setIsOpen(false)}
                        className={({ isActive }) => `
              flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors duration-200
              ${isActive
                                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                    : 'text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-white/5'
                                }
            `}
                    >
                        <Calculator className="w-5 h-5" />
                        <span className="font-medium">{t('nav.calculator', 'Calculator')}</span>
                    </NavLink>
                </div>
            </div>
        </nav>
    );
}
